import axios from './axios-interceptor';

// Module-level accumulators — persist for the entire page lifecycle.
// Not reset between calls so that a retry-success still shows hasErrors=true.
let _sessionErrors = [];
let _sentryEventIds = [];

// Tracks whether a terminal save (transaction/error) has fired,
// so the beforeunload handler doesn't fire a duplicate "abandoned" save.
let _sessionSaved = false;

/**
 * Record an error that occurred during this page session.
 * Call from captureError() or any error handler.
 *
 * @param {object} opts
 * @param {string} [opts.errorType]
 * @param {string} [opts.context]
 * @param {string} [opts.message]
 * @param {string} [opts.sentryEventId]
 */
export function trackSessionError({ errorType, context, message, sentryEventId } = {}) {
    _sessionErrors.push({
        errorType: errorType || null,
        context: context || null,
        message: message || null,
        timestamp: new Date().toISOString(),
        sentryEventId: sentryEventId || null,
    });
    if (sentryEventId) _sentryEventIds.push(sentryEventId);
}

/**
 * Fire-and-forget: saves the current LogRocket session URL to the backend.
 * Call after payments, errors, or other significant events.
 *
 * @param {string} apiUrl - Backend API base URL (e.g. data.api). Falls back to window.__apiUrl.
 * @param {object} opts
 * @param {string} [opts.userId]
 * @param {string} [opts.userTransactionsId]
 * @param {string} [opts.eventUrl]
 * @param {string} [opts.organizationId]
 * @param {string} [opts.eventType]   - 'transaction' | 'error' | 'login' etc.
 * @param {string} [opts.outcome]     - 'success' | 'failure' | 'unknown'
 * @param {object} [opts.metadata]    - Rich context: gateway, amount, payMethod, etc.
 */
export function saveLogRocketSession(apiUrl, opts = {}) {
    const url = apiUrl || window.__apiUrl;
    if (!url) return;

    // Mark terminal saves so beforeunload doesn't fire a duplicate "abandoned" save
    if (opts.eventType === 'transaction' || opts.eventType === 'error') {
        _sessionSaved = true;
    }

    const hasErrors = opts.eventType === 'error' || _sessionErrors.length > 0;
    const outcome = opts.outcome || (opts.eventType === 'error' ? 'failure' : 'success');

    const capturedErrors = _sessionErrors.length > 0 ? [..._sessionErrors] : null;
    const capturedSentryEventIds = [..._sentryEventIds];
    // Reset accumulators so subsequent saves don't duplicate events from this save
    _sessionErrors = [];
    _sentryEventIds = [];

    const send = (sessionUrl) => {
        if (!sessionUrl) {
            console.warn('[LogRocket] No session URL available — recording not saved');
            return;
        }
        const payload = {
            sessionUrl,
            userId: opts.userId || null,
            userTransactionsId: opts.userTransactionsId || null,
            eventUrl: opts.eventUrl || window.location.href,
            organizationId: opts.organizationId || null,
            eventType: opts.eventType || 'transaction',
            outcome,
            hasErrors,
            errorData: capturedErrors,
            metadata: {
                ...opts.metadata,
                page: window.location.pathname,
                sentryEventIds: capturedSentryEventIds,
            },
        };
        axios.post(`${url}logger-recordings`, payload).catch((e) => {
            console.error('[LogRocket] Failed to save session recording:', e?.message);
            // Fallback to sendBeacon (works during page teardown when axios may fail)
            try {
                navigator.sendBeacon(
                    `${url}logger-recordings`,
                    new Blob([JSON.stringify(payload)], { type: 'application/json' })
                );
            } catch (beaconErr) { /* fire-and-forget */ }
        });
    };

    const isValidSessionUrl = (u) => u && typeof u === 'string' && u.startsWith('https://');

    try {
        if (window.LogRocket && isValidSessionUrl(window.LogRocket.sessionURL)) {
            send(window.LogRocket.sessionURL);
        } else if (window.LogRocket && typeof window.LogRocket.getSessionURL === 'function') {
            let callbackFired = false;
            window.LogRocket.getSessionURL((sessionUrl) => {
                callbackFired = true;
                if (isValidSessionUrl(sessionUrl)) {
                    send(sessionUrl);
                } else {
                    console.warn('[LogRocket] getSessionURL callback returned invalid URL:', sessionUrl);
                }
            });
            // If getSessionURL never fires within 5s, log a warning for Sentry breadcrumbs
            setTimeout(() => {
                if (!callbackFired) {
                    console.warn('[LogRocket] getSessionURL callback did not fire within 5s — recording lost');
                }
            }, 5000);
        } else {
            console.warn('[LogRocket] LogRocket not available or missing getSessionURL — recording not saved');
        }
    } catch (e) {
        // Fire-and-forget — never break the payment flow
    }
}

/**
 * Register a `beforeunload` listener that fires a final "abandoned" save
 * if the customer closes/navigates away without completing a payment.
 * Uses `navigator.sendBeacon()` because axios won't complete during page teardown.
 *
 * @param {string} apiUrl - Backend API base URL
 */
export function enableAbandonmentTracking(apiUrl) {
    const url = apiUrl || window.__apiUrl;
    if (!url) return;

    window.addEventListener('beforeunload', () => {
        if (_sessionSaved) return; // payment already completed or errored

        const isValidSessionUrl = (u) => u && typeof u === 'string' && u.startsWith('https://');
        let sessionUrl = null;
        try {
            if (window.LogRocket && isValidSessionUrl(window.LogRocket.sessionURL)) {
                sessionUrl = window.LogRocket.sessionURL;
            }
        } catch (e) {}

        if (!sessionUrl) return;

        const payload = {
            sessionUrl,
            userId: null,
            userTransactionsId: null,
            eventUrl: window.location.href,
            organizationId: null,
            eventType: 'abandoned',
            outcome: 'abandoned',
            hasErrors: _sessionErrors.length > 0,
            errorData: _sessionErrors.length > 0 ? [..._sessionErrors] : null,
            metadata: {
                page: window.location.pathname,
                sentryEventIds: [..._sentryEventIds],
            },
        };

        try {
            navigator.sendBeacon(
                `${url}logger-recordings`,
                new Blob([JSON.stringify(payload)], { type: 'application/json' })
            );
        } catch (e) {}
    });
}
